// The default export rather than the legacy `parsePhoneNumberFromString` named one: the builder's
// bundle resolves libphonenumber-js to a build where that named export arrives undefined.
import parsePhoneNumber, { CountryCode } from 'libphonenumber-js';
import { AdCreativeType, FBCallToAction } from './AdCreative';
import { CALLS_CALL_TO_ACTION } from './CreativeImport';

/**
 * A calls ad is an ordinary link creative whose button is CALL_NOW. Meta reads the number off the
 * button's link as a `tel:` URI, and Ads Manager points the ad's own link at the Page, since there
 * is no website to send a caller to. Read back from live calls ads built in Ads Manager.
 */

/** Carousel and dynamic creatives are out: calls ads almost never use them (5 carousels in prod). */
export const CALL_CREATIVE_FORMATS: AdCreativeType[] = [
  AdCreativeType.single_image,
  AdCreativeType.single_video,
];

export const isCallCreative = (creative: {
  callToAction?: FBCallToAction | null;
}): boolean => creative.callToAction === CALLS_CALL_TO_ACTION;

/** E.164 for a dialable number, else undefined. Bare national numbers are read as `defaultCountry`. */
export const normalizeCallPhoneNumber = (
  raw?: string | null,
  defaultCountry: CountryCode = 'US',
): string | undefined => {
  if (!raw) return undefined;
  const parsed = parsePhoneNumber(raw, defaultCountry);
  return parsed?.isValid() ? parsed.number : undefined;
};

/** For display: "+16158768603" → "(615) 876-8603"; other countries in international format. */
export const formatCallPhoneNumber = (
  e164?: string | null,
): string | undefined => {
  if (!e164) return undefined;
  const parsed = parsePhoneNumber(e164);
  if (!parsed) return e164;
  return parsed.country === 'US'
    ? parsed.formatNational()
    : parsed.formatInternational();
};

/** Why a creative cannot run as a calls ad, or undefined when it can (or isn't one). */
export const callCreativeIssue = (creative: {
  format: AdCreativeType;
  callToAction?: FBCallToAction | null;
  callPhoneNumber?: string | null;
  /** The attached lead form, as the server's creative row names it. */
  leadgenFormUsedID?: number | null;
  /** The attached lead form, as the builder's creative model names it. */
  leadCardUsed?: unknown;
}): string | undefined => {
  if (!isCallCreative(creative)) return undefined;
  if (!CALL_CREATIVE_FORMATS.includes(creative.format)) {
    return 'Call Now is only available on single image and single video ads';
  }
  // A calls ad dials rather than opening a form, so the form is dropped when it publishes — and
  // on an Instant Forms campaign Meta then rejects the ad after the campaign already exists.
  if (creative.leadgenFormUsedID || creative.leadCardUsed) {
    return "Call Now can't be combined with a lead form";
  }
  if (!normalizeCallPhoneNumber(creative.callPhoneNumber)) {
    return 'Call Now needs a valid phone number';
  }
  return undefined;
};

export const callPhoneLink = (e164: string): string => `tel:${e164}`;

/** The number a synced CALL_NOW button dials, from its `tel:` link. */
export const callPhoneNumberFromLink = (
  link?: string | null,
): string | undefined =>
  link?.startsWith('tel:') ? link.slice('tel:'.length) : undefined;

/** Where a calls ad's own link points: the business's Page, not its website. */
export const callCreativePageLink = (fbPageID: string): string =>
  `https://facebook.com/${fbPageID}/`;
